import { useId, type ReactNode } from 'react';

/**
 * Form primitives.
 *
 * Every control pairs a slider with a number field. Dragging is how a design
 * gets found; typing is how it gets reproduced, and a configurator that only
 * offers one of the two is annoying in the other direction.
 */

interface FieldProps {
  label: string;
  help?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({ label, help, children, htmlFor }: FieldProps) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  help?: string;
  disabled?: boolean;
  onChange(value: number): void;
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  help,
  disabled,
  onChange,
}: SliderProps) {
  const id = useId();
  const decimals = step < 1 ? (String(step).split('.')[1]?.length ?? 2) : 0;

  return (
    <div className={`field field--slider${disabled ? ' is-disabled' : ''}`}>
      <div className="field__header">
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
        <div className="field__value">
          <input
            className="field__number"
            type="number"
            value={Number(value.toFixed(decimals))}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)));
            }}
          />
          {unit ? <span className="field__unit">{unit}</span> : null}
        </div>
      </div>
      <input
        id={id}
        className="slider"
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}

interface SelectProps<T extends string> {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  help?: string;
  onChange(value: T): void;
}

export function Select<T extends string>({
  label,
  value,
  options,
  help,
  onChange,
}: SelectProps<T>) {
  const id = useId();
  return (
    <Field label={label} help={help} htmlFor={id}>
      <select
        id={id}
        className="select"
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  help?: string;
  onChange(checked: boolean): void;
}

export function Toggle({ label, checked, help, onChange }: ToggleProps) {
  const id = useId();
  return (
    <div className="field field--toggle">
      <div className="toggle-row">
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={checked}
          className={`toggle${checked ? ' is-on' : ''}`}
          onClick={() => onChange(!checked)}
        >
          <span className="toggle__knob" />
        </button>
      </div>
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}

interface SegmentedProps<T extends string> {
  label?: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string; help?: string }>;
  onChange(value: T): void;
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedProps<T>) {
  return (
    <div className="field">
      {label ? <span className="field__label">{label}</span> : null}
      <div className="segmented" role="tablist">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={value === option.value}
            title={option.help}
            className={`segmented__item${value === option.value ? ' is-active' : ''}`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface AngleDialProps {
  label: string;
  value: number;
  help?: string;
  onChange(value: number): void;
}

/** A degree slider paired with a dial, so the direction is visible at a glance. */
export function AngleDial({ label, value, help, onChange }: AngleDialProps) {
  const radians = (value * Math.PI) / 180;

  return (
    <div className="field field--angle">
      <div className="field__header">
        <span className="field__label">{label}</span>
        <div className="field__value">
          <input
            className="field__number"
            type="number"
            value={Math.round(value)}
            min={0}
            max={359}
            step={1}
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isFinite(next)) onChange(((next % 360) + 360) % 360);
            }}
          />
          <span className="field__unit">°</span>
        </div>
      </div>
      <div className="angle-row">
        <svg className="dial" viewBox="-12 -12 24 24" aria-hidden="true">
          <circle className="dial__ring" cx="0" cy="0" r="10" />
          <line
            className="dial__needle"
            x1="0"
            y1="0"
            x2={Math.cos(radians) * 9}
            y2={-Math.sin(radians) * 9}
          />
        </svg>
        <input
          className="slider"
          type="range"
          value={value}
          min={0}
          max={359}
          step={1}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </div>
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}

export function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <header className="section__header">
        <h3 className="section__title">{title}</h3>
        {description ? <p className="section__description">{description}</p> : null}
      </header>
      <div className="section__body">{children}</div>
    </section>
  );
}

interface SwatchesProps {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  help?: string;
  onChange(value: string): void;
}

/**
 * A row of colour circles.
 *
 * A colour is the one thing a text label is worse at than the thing itself, and
 * five named options in a narrow panel truncate to "Pl…", "Bo…", "Gr…".
 */
export function Swatches({ label, value, options, help, onChange }: SwatchesProps) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="swatches" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === value}
            aria-label={option.label}
            title={option.label}
            className={`swatch${option.value === value ? ' is-active' : ''}`}
            style={{ background: option.value }}
            onClick={() => onChange(option.value)}
          />
        ))}
      </div>
      {help ? <p className="field__help">{help}</p> : null}
    </div>
  );
}
